import { useState } from "react";
import { Plus, Trash2, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, toList } from "../../lib/api";
import { useApi } from "../../hooks/useApi";
import { Panel, Loader, ErrorNote, Empty, Btn, shortDate, fmt } from "./kit";
import { TextInput } from "./Form";

const useSubmit = (fn, reload) => {
  const [busy, setBusy] = useState(false);
  const run = async (...a) => {
    setBusy(true);
    try { await fn(...a); reload(); return true; } catch (e) { toast.error(errMsg(e)); return false; } finally { setBusy(false); }
  };
  return [run, busy];
};

const GoalForm = ({ tid, pid, reload }) => {
  const blank = { title: "", metric: "", baseline_value: "", target_value: "", target_date: "" };
  const [f, setF] = useState(blank);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const num = (v) => (v === "" ? null : Number(v));
  const [save, busy] = useSubmit(() => api.post(`/teams/${tid}/goals`, {
    player_id: pid, title: f.title.trim(), metric: f.metric.trim() || null, baseline_value: num(f.baseline_value),
    target_value: num(f.target_value), target_date: f.target_date || null, status: "active",
  }), reload);
  const submit = async (e) => { e.preventDefault(); if (!f.title.trim()) return toast.error("Name the goal"); if (await save()) setF(blank); };
  return (
    <form onSubmit={submit} data-testid="goal-form" className="mb-5 grid gap-2 sm:grid-cols-6">
      <TextInput data-testid="goal-title-input" value={f.title} onChange={set("title")} placeholder="Shoot 75% from the line" className="py-2 sm:col-span-6" />
      <TextInput data-testid="goal-metric-input" value={f.metric} onChange={set("metric")} placeholder="Metric (FT%)" className="py-2 sm:col-span-2" />
      <TextInput data-testid="goal-baseline-input" type="number" value={f.baseline_value} onChange={set("baseline_value")} placeholder="Now" className="py-2" />
      <TextInput data-testid="goal-target-input" type="number" value={f.target_value} onChange={set("target_value")} placeholder="Target" className="py-2" />
      <TextInput data-testid="goal-date-input" type="date" value={f.target_date} onChange={set("target_date")} className="py-2 sm:col-span-2" />
      <Btn type="submit" disabled={busy} data-testid="goal-add" className="px-4 py-2 text-xs sm:col-span-6 sm:justify-self-end"><Plus className="h-3.5 w-3.5" />Add goal</Btn>
    </form>
  );
};

const Goals = ({ tid, pid, canEdit }) => {
  const r = useApi(`/teams/${tid}/players/${pid}/goals`);
  const list = toList(r.data, ["goals"]);
  const [toggle] = useSubmit((g) => api.patch(`/teams/${tid}/goals/${g.id}`, { status: g.status === "completed" ? "active" : "completed" }), r.reload);
  const [remove] = useSubmit((g) => api.delete(`/teams/${tid}/goals/${g.id}`), r.reload);
  return (
    <Panel>
      <h3 className="mb-4 font-display text-xl font-bold uppercase text-white">Goals</h3>
      {canEdit && <GoalForm tid={tid} pid={pid} reload={r.reload} />}
      {r.loading && !r.data ? <Loader /> : r.error ? <ErrorNote error={r.error} /> : !list.length ? <Empty title="No goals yet" /> : (
        <ul className="space-y-2">
          {list.map((g) => {
            const done = g.status === "completed";
            return (
              <li key={g.id} data-testid={`goal-item-${g.id}`} className="flex items-start gap-3 rounded-xl bg-[#162032] px-4 py-3">
                <button disabled={!canEdit} data-testid={`goal-toggle-${g.id}`} onClick={() => toggle(g)} aria-label="Toggle complete" className="mt-0.5 shrink-0">
                  {done ? <CheckCircle2 className="h-5 w-5 text-[#39FF14]" /> : <Circle className="h-5 w-5 text-slate-500" />}
                </button>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-semibold ${done ? "text-slate-400 line-through" : "text-white"}`}>{g.title}</p>
                  <p className="font-mono-gd text-[11px] uppercase tracking-wider text-slate-400">
                    {[g.metric, g.baseline_value != null && `from ${fmt(g.baseline_value)}`, g.target_value != null && `to ${fmt(g.target_value)}`, g.target_date && `by ${shortDate(g.target_date)}`].filter(Boolean).join(" · ") || g.status}
                  </p>
                </div>
                {canEdit && <button data-testid={`goal-delete-${g.id}`} onClick={() => window.confirm("Delete this goal?") && remove(g)} className="shrink-0 p-1 text-slate-400 hover:text-red-300" aria-label="Delete goal"><Trash2 className="h-4 w-4" /></button>}
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
};

const Drills = ({ tid, pid, canEdit }) => {
  const r = useApi(`/teams/${tid}/players/${pid}/drills`);
  const list = toList(r.data, ["drills"]).sort((a, b) => String(b.date ?? "").localeCompare(String(a.date ?? "")));
  const [f, setF] = useState({ drill: "", value: "", date: "" });
  const [save, busy] = useSubmit(() => api.post(`/teams/${tid}/drills`, { player_id: pid, drill: f.drill.trim(), value: f.value.trim(), date: f.date || null }), r.reload);
  const [remove] = useSubmit((d) => api.delete(`/teams/${tid}/drills/${d.id}`), r.reload);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.drill.trim() || !f.value.trim()) return toast.error("Add the drill and a result or assignment");
    if (await save()) setF({ drill: "", value: "", date: "" });
  };
  return (
    <Panel>
      <h3 className="mb-4 font-display text-xl font-bold uppercase text-white">Drills</h3>
      {canEdit && (
        <form onSubmit={submit} data-testid="drill-form" className="mb-5 grid gap-2 sm:grid-cols-[2fr_2fr_1fr_auto]">
          <TextInput data-testid="drill-name-input" value={f.drill} onChange={(e) => setF({ ...f, drill: e.target.value })} placeholder="Mikan drill" className="py-2" />
          <TextInput data-testid="drill-value-input" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} placeholder="3 x 50 makes / 42 in 60s" className="py-2" />
          <TextInput data-testid="drill-date-input" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="py-2" />
          <Btn type="submit" disabled={busy} data-testid="drill-add" className="px-4 py-2 text-xs"><Plus className="h-3.5 w-3.5" />Add</Btn>
        </form>
      )}
      {r.loading && !r.data ? <Loader /> : r.error ? <ErrorNote error={r.error} /> : !list.length ? <Empty title="No drills yet" /> : (
        <ul className="divide-y divide-[#1E293B]">
          {list.map((d) => (
            <li key={d.id} data-testid={`drill-item-${d.id}`} className="flex items-center justify-between gap-3 py-2.5 text-sm">
              <span className="min-w-0"><span className="font-semibold text-white">{d.drill}</span><span className="ml-2 text-slate-300">{d.value}</span></span>
              <span className="flex shrink-0 items-center gap-2 font-mono-gd text-[11px] uppercase text-slate-400">{shortDate(d.date)}
                {canEdit && <button data-testid={`drill-delete-${d.id}`} onClick={() => window.confirm("Delete this drill?") && remove(d)} className="p-1 hover:text-red-300" aria-label="Delete drill"><Trash2 className="h-4 w-4" /></button>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
};

/** Goals and drills on a player profile. Coaches create/complete/delete; everyone else reads. */
export const DevelopmentPlan = ({ tid, pid, canEdit }) => (
  <div className="grid gap-6 xl:grid-cols-2">
    <Goals tid={tid} pid={pid} canEdit={canEdit} />
    <Drills tid={tid} pid={pid} canEdit={canEdit} />
  </div>
);
